import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { apiKeyService } from '../services/apiKeyService';
import { useContextStore } from '../store/contextStore';
import { useNotificationStore } from '../store/notificationStore';
import type { ProjectApiKeyDto, CreateApiKeyRequest, GenerateApiKeyResponse } from '../types/apiKey';
import { TextField } from '../@core/components/mui';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import { PageListLayout } from '../components/layouts/PageListLayout';

/**
 * API Key list page component.
 */
export const ApiKeyListPage = () => {
  const { currentProject } = useContextStore();
  const [apiKeys, setApiKeys] = useState<ProjectApiKeyDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openForm, setOpenForm] = useState(false);
  const [openKeyDialog, setOpenKeyDialog] = useState(false);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [showKey, setShowKey] = useState(false);
  const [formData, setFormData] = useState<CreateApiKeyRequest>({
    name: '',
    expiresAt: undefined,
    scopes: [],
  });
  const { showNotification } = useNotificationStore();

  const loadApiKeys = async () => {
    if (!currentProject) {
      setError('Please select a project first');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const keys = await apiKeyService.getApiKeys(currentProject.id);
      setApiKeys(keys);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load API keys');
      showNotification(err.response?.data?.message || 'Failed to load API keys', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadApiKeys();
  }, [currentProject]);

  const handleCreate = () => {
    setFormData({ name: '', expiresAt: undefined, scopes: [] });
    setOpenForm(true);
  };

  const handleGenerate = async () => {
    if (!currentProject || !formData.name) {
      setError('Please provide a name for the API key');
      return;
    }

    try {
      const response: GenerateApiKeyResponse = await apiKeyService.generateApiKey(
        currentProject.id,
        formData
      );
      setGeneratedKey(response.fullKey);
      setOpenForm(false);
      setOpenKeyDialog(true);
      loadApiKeys();
      showNotification('API key generated successfully', 'success');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate API key');
      showNotification(err.response?.data?.message || 'Failed to generate API key', 'error');
    }
  };

  const handleCopyKey = async () => {
    if (generatedKey) {
      try {
        await navigator.clipboard.writeText(generatedKey);
        showNotification('API key copied to clipboard', 'success');
      } catch {
        showNotification('Failed to copy to clipboard', 'error');
      }
    }
  };

  const handleRevoke = async (id: string) => {
    if (!currentProject) return;

    if (!window.confirm('Are you sure you want to revoke this API key?')) {
      return;
    }

    try {
      await apiKeyService.revokeApiKey(currentProject.id, id);
      showNotification('API key revoked successfully', 'success');
      loadApiKeys();
    } catch (err: any) {
      showNotification(err.response?.data?.message || 'Failed to revoke API key', 'error');
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  // Define table columns
  const tableColumns: DataTableColumn<ProjectApiKeyDto>[] = [
    {
      id: 'name',
      label: 'Name',
      minWidth: 200,
    },
    {
      id: 'keyPrefix',
      label: 'Key Prefix',
      minWidth: 200,
      render: (key) => (
        <Typography variant="body2" fontFamily="monospace">
          {key.keyPrefix}****
        </Typography>
      ),
    },
    {
      id: 'createdAt',
      label: 'Created',
      minWidth: 120,
      render: (key) => formatDate(key.createdAt),
    },
    {
      id: 'lastUsedAt',
      label: 'Last Used',
      minWidth: 120,
      render: (key) => formatDate(key.lastUsedAt),
    },
    {
      id: 'expiresAt',
      label: 'Expires',
      minWidth: 120,
      render: (key) => formatDate(key.expiresAt),
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 100,
      render: (key) => (
        <Chip
          label={key.isActive ? 'Active' : 'Revoked'}
          color={key.isActive ? 'success' : 'error'}
          size="small"
        />
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 100,
      align: 'right',
      render: (key) => (
        <Tooltip title="Revoke">
          <IconButton
            size="small"
            onClick={() => handleRevoke(key.id)}
            color="error"
            disabled={!key.isActive}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  if (!currentProject) {
    return (
      <Box sx={{ p: 6 }}>
        <Alert severity="info">Please select a project first to view API keys.</Alert>
      </Box>
    );
  }

  return (
    <>
      <PageListLayout
        title={`API Keys - ${currentProject.name}`}
        createButton={{ label: 'Generate API Key', onClick: handleCreate, icon: <AddIcon /> }}
        error={error || undefined}
        onErrorDismiss={() => setError(null)}
        loading={loading}
        isEmpty={apiKeys.length === 0}
        emptyState={{
          title: 'No API keys found',
          description: 'Generate a new API key to get started.',
          action: { label: 'Generate API Key', onClick: handleCreate },
        }}
      >
        <DataTable
          columns={tableColumns}
          rows={apiKeys}
        />
      </PageListLayout>

      {/* Create API Key Dialog */}
      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate API Key</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
              helperText="A descriptive name for this API key"
            />
            <TextField
              label="Expiration Date"
              type="datetime-local"
              value={formData.expiresAt || ''}
              onChange={(e) =>
                setFormData({ ...formData, expiresAt: e.target.value || undefined })
              }
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Optional: Leave empty for no expiration"
            />
            <FormControl fullWidth>
              <InputLabel>Scopes</InputLabel>
              <Select
                multiple
                value={formData.scopes}
                onChange={(e) =>
                  setFormData({ ...formData, scopes: e.target.value as string[] })
                }
                renderValue={(selected) => (selected as string[]).join(', ')}
              >
                <MenuItem value="read">Read</MenuItem>
                <MenuItem value="write">Write</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
            <Alert severity="warning">
              You will only be able to view the full API key once. Make sure to copy it before closing this dialog.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleGenerate} disabled={!formData.name}>
            Generate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Display Generated Key Dialog */}
      <Dialog open={openKeyDialog} onClose={() => setOpenKeyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>API Key Generated</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This is the only time you will be able to view the full API key. Copy it now and store it securely.
          </Alert>
          <TextField
            label="API Key"
            value={generatedKey || ''}
            fullWidth
            multiline
            rows={3}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowKey(!showKey)} edge="end">
                    {showKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                  <IconButton onClick={handleCopyKey} edge="end">
                    <CopyIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            type={showKey ? 'text' : 'password'}
            sx={{ fontFamily: 'monospace' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenKeyDialog(false)} variant="contained">
            I've Copied the Key
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
